import { useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { get } from "../api.js";
import { isOwner, useAuth } from "../auth.js";
import { formatDateTime } from "../format.js";
import { Pagination } from "../Pagination.js";
import { CARD_RECORD_PAGE_SIZE } from "../paginationPageSizes.js";
import { useAsync } from "../useAsync.js";
import { useStudentCreditReads } from "../booking/StudentCreditReads.js";
import { STUDENT_CREDIT_STATE_CHANGED_EVENT, studentCreditStateChangedFor } from "../booking/studentCreditEvents.js";
import { canViewStudentOverviewCredits } from "./StudentOverviewCredits.js";
import "./adminFamilyOverview.css";

type Account = {userId:string; email:string|null; username:string|null; confirmed:boolean};
type Member = {id:string; fullName:string; status:string; instrument:string|null; privateBooking:boolean;
  recordHref:string; upcomingCount:number; historyCount:number; nextLessonAt:string|null};
export interface AdminFamilyOverviewData {
  anchor:{id:string; fullName:string}; accounts:Account[]; account:Account|null; members:Member[];
  direction:"upcoming"|"history"; studentId:string|null; page:number; limit:number; total:number;
  lessons:Array<{id:string; studentId:string; bookingId:string|null; startsAt:string; endsAt:string;
    ameliaAppointmentId?:number|null;
    serviceName:string|null; tutorName:string|null; status:string; appointmentStatus:string;
    domain:"private"|"school"; recordHref:string}>;
  nextWeekLessons?: AdminFamilyOverviewData["lessons"];
}

function creditQueue() {
  let active=0;
  const waiting:Array<()=>void>=[];
  return async <T,>(read:()=>Promise<T>):Promise<T>=>{
    if(active>=3)await new Promise<void>(resolve=>waiting.push(resolve));
    else active++;
    try{return await read();}finally{const next=waiting.shift();if(next)next();else active--;}
  };
}

function statusLabel(status:string, appointmentStatus:string) {
  const states=[status.toLowerCase(),appointmentStatus.toLowerCase()];
  if (states.some(s=>s==="canceled"||s==="cancelled")) return "Cancelled";
  if (states.includes("no-show")) return "No-show";
  if (states.includes("rejected")) return "Rejected";
  if (states.includes("pending")) return "Awaiting confirmation";
  if (["approved","scheduled","active","confirmed","booked"].includes(status)) return "Booked";
  return status.replaceAll("_"," ").replace(/^./,c=>c.toUpperCase());
}

export function AdminFamilyOverview({studentId, refreshRevision=0}:{studentId:string;refreshRevision?:number}) {
  const {me}=useAuth();
  return isOwner(me) ? <FamilyOverviewContent key={`${me?.user.id}:${studentId}`} studentId={studentId} refreshRevision={refreshRevision}/> : null;
}

function FamilyOverviewContent({studentId,refreshRevision}:{studentId:string;refreshRevision:number}) {
  const [parentUserId,setParentUserId]=useState<string|null>(null);
  const [filterStudentId,setFilterStudentId]=useState<string|null>(null);
  const [direction,setDirection]=useState<"upcoming"|"history">("upcoming");
  const [page,setPage]=useState(1);
  const headingId=useId(),accountId=useId(),filterId=useId();
  const credits=useStudentCreditReads();
  const loadCredit=useMemo(creditQueue,[]);
  const family=useAsync(()=>get<AdminFamilyOverviewData>(`/admin/students/${encodeURIComponent(studentId)}/family-overview`,{
    parentUserId:parentUserId??undefined,studentId:filterStudentId??undefined,direction,page:String(page),limit:String(CARD_RECORD_PAGE_SIZE),
  }),[studentId,parentUserId,filterStudentId,direction,page,refreshRevision],{reloadOnMirrorRefresh:true});
  const data=!family.loading&&!family.error&&family.data?.anchor.id===studentId&&
    family.data.direction===direction&&family.data.studentId===filterStudentId&&family.data.page===page&&
    (parentUserId===null||family.data.account?.userId===parentUserId) ? family.data : null;
  const memberKey=family.data?.anchor.id===studentId ? family.data.members.map(m=>m.id).sort().join(",") : "";
  useEffect(()=>{
    const changed=(event:Event)=>{
      if (!memberKey.split(",").some(id=>studentCreditStateChangedFor(event,id))) return;
      credits.invalidate(); family.reload();
    };
    window.addEventListener(STUDENT_CREDIT_STATE_CHANGED_EVENT,changed);
    return ()=>window.removeEventListener(STUDENT_CREDIT_STATE_CHANGED_EVENT,changed);
  },[memberKey,credits.invalidate,family.reload]);
  const pages=data?Math.max(1,Math.ceil(data.total/data.limit)):1;
  useEffect(()=>{if(data&&page>pages)setPage(pages);},[data,page,pages]);
  if(data?.accounts.length===0)return null;

  return <section id="student-family-overview" tabIndex={-1} className="panel admin-family-overview" aria-labelledby={headingId}>
    <header className="admin-family-heading">
      <div><span className="admin-family-eyebrow">Admin overview</span><h2 id={headingId}>Family account</h2>
        <p className="muted small">Trace linked students’ lessons together. Bookings, payments and credits stay on each student’s record.</p></div>
      <button type="button" className="action-tertiary small" disabled={family.loading} onClick={()=>{credits.invalidate();family.reload();}}>Refresh family</button>
    </header>
    {family.error ? <div className="error" role="alert">We could not load this family account. <button type="button" className="quiet" onClick={family.reload}>Try again</button>
      {(parentUserId||filterStudentId)&&<button type="button" className="quiet" onClick={()=>{setParentUserId(null);setFilterStudentId(null);setPage(1);}}>Reset family filters</button>}</div>
      : !data ? <p role="status" className="muted">Loading family lessons…</p>
      : data.account && <>
        <div className="admin-family-account">
          {data.accounts.length>1 ? <div><label htmlFor={accountId}>Parent account</label>
            <select id={accountId} value={data.account.userId} onChange={event=>{setParentUserId(event.target.value);setFilterStudentId(null);setPage(1);}}>
              {data.accounts.map(account=><option key={account.userId} value={account.userId}>{account.email??account.username??"Parent account"}</option>)}
            </select></div> : <div><span className="small muted">Parent account</span><strong>{data.account.email??data.account.username??"Parent account"}</strong></div>}
          <span className="tag">{data.account.confirmed?"Confirmed account":"Awaiting email confirmation"}</span>
        </div>
        <div className="admin-family-members" aria-label="Linked student records">
          {data.members.map(member=><FamilyMemberCard key={member.id} member={member} current={member.id===studentId} loadCredit={loadCredit}/>)}
        </div>
        <div className="admin-family-timeline-heading"><h3>Family lessons</h3>
          <p className="small muted">Every lesson is labelled with the student it belongs to. Open their record to manage it.</p></div>
        <div className="admin-family-filters">
          <div className="admin-family-directions" role="group" aria-label="Family lesson period">
            {(["upcoming","history"] as const).map(value=><button type="button" key={value}
              className="action-tertiary small" aria-pressed={direction===value}
              onClick={()=>{setDirection(value);setPage(1);}}>{value==="upcoming"?"Upcoming":"History"}</button>)}
          </div>
          <div><label htmlFor={filterId}>Student</label><select id={filterId} value={filterStudentId??""}
            onChange={event=>{setFilterStudentId(event.target.value||null);setPage(1);}}>
            <option value="">All linked students</option>
            {data.members.map(member=><option key={member.id} value={member.id}>{member.fullName}</option>)}
          </select></div>
        </div>
        {data.lessons.length===0 ? <p className="empty">{direction==="upcoming"?"No upcoming lessons for this selection.":"No lesson history for this selection."}</p>
          : <ul className="admin-family-lessons" aria-label={direction==="upcoming"?"Upcoming family lessons":"Family lesson history"}>
            {data.lessons.map(lesson=>{
              const member=data.members.find(m=>m.id===lesson.studentId);
              return <li key={lesson.id}>
                <div className="admin-family-lesson-person"><Link to={lesson.recordHref} className="admin-family-student-name">{member?.fullName??"Student"}</Link>
                  <span className="small muted">{lesson.domain==="school"?"School lesson":"Private lesson"}</span></div>
                <div className="admin-family-lesson-detail"><time dateTime={lesson.startsAt}>{formatDateTime(lesson.startsAt)}</time>
                  <span>{lesson.serviceName??"Lesson"}{lesson.tutorName?` · ${lesson.tutorName}`:""}</span></div>
                <span className="tag">{statusLabel(lesson.status,lesson.appointmentStatus)}</span>
              </li>;
            })}
          </ul>}
        <p className="small muted" role="status">{data.total===0?"0 lessons":`${(page-1)*data.limit+1}–${Math.min(page*data.limit,data.total)} of ${data.total} lessons`}</p>
        <Pagination page={page} pages={pages} onPageChange={setPage} ariaLabel="Family lessons pages"
          previousAriaLabel="Previous family lessons page" nextAriaLabel="Next family lessons page"/>
        <p className="admin-family-record-note small muted">The individual lesson controls below apply to <strong>{data.anchor.fullName}</strong> only.</p>
      </>}
  </section>;
}

function FamilyMemberCard({member,current,loadCredit}:{member:Member;current:boolean;loadCredit:ReturnType<typeof creditQueue>}) {
  const {me}=useAuth();
  const reads=useStudentCreditReads();
  const showCredits=member.privateBooking&&canViewStudentOverviewCredits(me);
  const credits=useAsync(async()=>{
    if(!showCredits)return null;
    const result=await loadCredit(()=>reads.lessonCredits(member.id));
    if(result.portal_student_id!==member.id)throw new Error("Credit record did not match the student.");
    return result;
  },[member.id,showCredits,reads,loadCredit]);
  const balance=credits.data?.portal_student_id===member.id&&!credits.error?credits.data:null;
  if(current&&member.upcomingCount===0&&member.historyCount===0&&(!showCredits||(!credits.loading&&balance?.total_available_credits===0))) {
    return <aside className="admin-family-empty-record small muted"><strong>Opened record: {member.fullName}.</strong> No lessons or available credits are recorded here. Linked students are shown separately.</aside>;
  }
  return <article className="admin-family-member" aria-label={`${member.fullName} student record`}>
    <div className="admin-family-member-title"><h3><Link to={member.recordHref}>{member.fullName}</Link></h3>
      {current&&<span className="tag">Opened record</span>}{member.status!=="active"&&<span className="tag">{member.status}</span>}</div>
    <p className="small muted">{member.instrument??(member.privateBooking?"Private lessons":"School lessons")}</p>
    <dl><div><dt>Upcoming</dt><dd>{member.upcomingCount} {member.upcomingCount===1?"lesson":"lessons"}</dd></div>
      <div><dt>Next lesson</dt><dd>{member.nextLessonAt?<time dateTime={member.nextLessonAt}>{formatDateTime(member.nextLessonAt)}</time>:"None booked"}</dd></div>
      {showCredits&&<div><dt>Available credits</dt><dd>{credits.loading?"Loading…":credits.error?<>Unavailable <button type="button" className="quiet" onClick={()=>{reads.invalidate();credits.reload();}}>Retry</button></>:balance?balance.total_available_credits:"Unavailable"}</dd></div>}
    </dl>
    <div className="admin-family-member-links"><Link to={member.recordHref}>Open student record</Link>
      {member.privateBooking&&<Link to={`${member.recordHref}?tab=admin`}>Payments and credits</Link>}</div>
  </article>;
}
